import type { WebhookEndpointHealth } from '@compass/client/types/webhook';
import React from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useStore } from '@/store';
import { getRelativeTime } from '@/utils/date';

import {
  backlogCount,
  EMPTY_VALUE,
  formatLatencyMs,
  formatSuccessRate,
  successRateTone,
} from '../utils';

interface EndpointHealthTilesProps {
  health: WebhookEndpointHealth | undefined;
  isLoading: boolean;
  /** The dispatcher could not be reached; tiles show "—" with a note. */
  unavailable: boolean;
}

function Tile({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'default' | 'warn' | 'bad';
}) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums',
          tone === 'warn' && 'text-attention-foreground',
          tone === 'bad' && 'text-destructive',
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Success rate, p95 latency, backlog and last success over the last 24 hours. */
export function EndpointHealthTiles({ health, isLoading, unavailable }: EndpointHealthTilesProps) {
  const { getTimeZone } = useStore();
  const timeZone = getTimeZone();

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} className="h-[84px] w-full" />
        ))}
      </div>
    );
  }

  const tone = successRateTone(health?.success_rate);
  const backlog = backlogCount(health);
  const failures = health?.consecutive_failures ?? 0;

  return (
    <div className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Success rate (24h)"
          value={formatSuccessRate(health?.success_rate)}
          hint={
            health?.attempts ? `${health.attempts.toLocaleString('en-US')} attempts` : undefined
          }
          tone={tone === 'bad' ? 'bad' : tone === 'warn' ? 'warn' : 'default'}
        />
        <Tile label="p95 latency" value={formatLatencyMs(health?.p95_latency_ms)} />
        <Tile
          label="Backlog"
          value={health ? backlog.toLocaleString('en-US') : EMPTY_VALUE}
          hint={
            health
              ? `${health.retrying ?? 0} retrying · ${health.exhausted ?? 0} exhausted${
                  health.oldest_undelivered_at
                    ? ` · oldest ${getRelativeTime(health.oldest_undelivered_at, timeZone)}`
                    : ''
                }`
              : undefined
          }
          tone={(health?.exhausted ?? 0) > 0 ? 'bad' : backlog > 0 ? 'warn' : 'default'}
        />
        <Tile
          label="Last success"
          value={
            health?.last_success_at ? (
              <span className="text-base">{getRelativeTime(health.last_success_at, timeZone)}</span>
            ) : (
              EMPTY_VALUE
            )
          }
          hint={
            health
              ? failures > 0
                ? `${failures} consecutive ${failures === 1 ? 'failure' : 'failures'}`
                : 'No failures since'
              : undefined
          }
          tone={failures > 0 ? 'bad' : 'default'}
        />
      </div>
      {unavailable && (
        <p className="text-xs text-muted-foreground">
          Health is temporarily unavailable. Endpoint settings still work.
        </p>
      )}
    </div>
  );
}
